import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation, useQueryClient } from 'react-query'
import { reportError } from 'utils/error'
import { BrandPlanSchedule, SectionKey } from 'types'
import { scheduleKeys } from '../schedules/scheduleKeys'

const parseErrorMessage = (data: any): string => {
  return data?.errors?.[0] || data?.error || 'Failed to regenerate topics'
}

export interface Data {
  schedule: BrandPlanSchedule
}

interface RegenerateTopicsParams {
  workspaceId: number
  scheduleId: number
  sections?: SectionKey[]
  /** If false, changes are applied to the DB automatically */
  preview?: boolean
  /** Whether to regenerate content after the topics are updated */
  regenerateContentAfter?: boolean
  /** IDs of ScheduleContentContextSource records to include as extra source materials */
  contextSourceIds?: number[]
}

export type RegenerateTopicsVariables = Omit<RegenerateTopicsParams, 'workspaceId'>

const regenerateTopics = async ({
  workspaceId,
  scheduleId,
  sections,
  preview = true,
  regenerateContentAfter = false,
  contextSourceIds,
}: RegenerateTopicsParams) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_plan_schedules/${scheduleId}/regenerate_topics`, {
    method: 'POST',
    body: {
      sections: sections || [],
      preview,
      regenerate_content_after: regenerateContentAfter,
      ...(contextSourceIds?.length ? { context_source_ids: contextSourceIds } : {}),
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(parseErrorMessage(res.data))
  }

  return res.data
}

interface UseRegenerateBrandPlanScheduleTopicsOptions {
  onError?: (error: Error) => void
  onSuccess?: () => void
}

export const useRegenerateBrandPlanScheduleTopics = (options?: UseRegenerateBrandPlanScheduleTopicsOptions) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, RegenerateTopicsVariables>(
    ({ scheduleId, sections, regenerateContentAfter, preview, contextSourceIds }) =>
      regenerateTopics({ workspaceId, scheduleId, sections, regenerateContentAfter, preview, contextSourceIds }),
    {
      onError: (error) => {
        reportError(error)
        options?.onError?.(error)
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(scheduleKeys.detail(variables.scheduleId))
        queryClient.invalidateQueries(['preview_topics', variables.scheduleId])
        options?.onSuccess?.()
      },
    },
  )
}
